import React, { useMemo, useCallback } from 'react';
import Model from 'react-body-highlighter';
import { useTheme } from '../../contexts/ThemeContext';

interface BodyMapSVGProps {
  view: 'front' | 'back';
  regionTermCounts: Map<string, number>;
  selectedRegion: string | null;
  onRegionClick: (regionId: string) => void;
  onRegionHover: (regionId: string | null) => void;
}

// Library muscle slugs
type Muscle =
  | 'trapezius' | 'upper-back' | 'lower-back' | 'chest' | 'biceps' | 'triceps'
  | 'forearm' | 'back-deltoids' | 'front-deltoids' | 'abs' | 'obliques'
  | 'adductor' | 'abductors' | 'hamstring' | 'quadriceps' | 'calves'
  | 'gluteal' | 'head' | 'neck' | 'knees' | 'left-soleus' | 'right-soleus';

// Our region IDs → library muscle slugs
const REGION_TO_MUSCLES: Record<string, Muscle[]> = {
  'head-brain':            ['head'],
  'mental-health':         ['head'],
  'eyes':                  ['head'],
  'neck-throat':           ['neck'],
  'chest-lungs':           ['chest'],
  'heart-cardiovascular':  ['chest'],
  'abdomen-gi':            ['abs', 'obliques'],
  'pelvis-urogenital':     ['adductor', 'abductors'],
  'spine-back':            ['trapezius', 'upper-back', 'lower-back'],
  'upper-limbs':           ['biceps', 'triceps', 'forearm', 'front-deltoids', 'back-deltoids'],
  'lower-limbs':           ['quadriceps', 'hamstring', 'calves', 'gluteal', 'knees', 'left-soleus', 'right-soleus'],
};

// Reverse: library muscle → our primary region (for click handling)
const MUSCLE_TO_REGION: Record<string, string> = {
  head:             'head-brain',
  neck:             'neck-throat',
  chest:            'chest-lungs',
  abs:              'abdomen-gi',
  obliques:         'abdomen-gi',
  adductor:         'pelvis-urogenital',
  abductors:        'pelvis-urogenital',
  trapezius:        'spine-back',
  'upper-back':     'spine-back',
  'lower-back':     'spine-back',
  biceps:           'upper-limbs',
  triceps:          'upper-limbs',
  forearm:          'upper-limbs',
  'front-deltoids': 'upper-limbs',
  'back-deltoids':  'upper-limbs',
  quadriceps:       'lower-limbs',
  hamstring:        'lower-limbs',
  calves:           'lower-limbs',
  gluteal:          'lower-limbs',
  knees:            'lower-limbs',
  'left-soleus':    'lower-limbs',
  'right-soleus':   'lower-limbs',
};

export const BodyMapSVG: React.FC<BodyMapSVGProps> = ({
  view,
  regionTermCounts,
  selectedRegion: _selectedRegion,
  onRegionClick,
  onRegionHover: _onRegionHover,
}) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  // Convert our regionTermCounts → library IExerciseData[]
  const data = useMemo(() => {
    const exercises: { name: string; muscles: Muscle[]; frequency: number }[] = [];

    regionTermCounts.forEach((count, regionId) => {
      if (count === 0) return;
      const muscles = REGION_TO_MUSCLES[regionId];
      if (!muscles) return;
      exercises.push({ name: regionId, muscles, frequency: count });
    });

    return exercises;
  }, [regionTermCounts]);

  // Red gradient — high contrast, medical heat-map style
  const highlightedColors = isDark
    ? ['#7f1d1d', '#991b1b', '#b91c1c', '#dc2626', '#ef4444', '#f87171', '#fca5a5']
    : ['#fca5a5', '#f87171', '#ef4444', '#dc2626', '#b91c1c', '#991b1b', '#7f1d1d'];

  const bodyColor = isDark ? '#475569' : '#e2e8f0';

  const handleClick = useCallback((muscleStats: any) => {
    const muscle = muscleStats?.muscle as string;
    if (muscle) {
      const regionId = MUSCLE_TO_REGION[muscle];
      if (regionId) onRegionClick(regionId);
    }
  }, [onRegionClick]);

  return (
    <div className="body-map-container flex items-center justify-center">
      <style>{`
        .body-map-container .rbh polygon {
          transition: filter 0.15s ease, opacity 0.15s ease;
        }
        .body-map-container .rbh polygon:hover {
          filter: brightness(1.2) saturate(1.3);
        }
      `}</style>
      <Model
        type={view === 'front' ? 'anterior' : 'posterior'}
        data={data}
        bodyColor={bodyColor}
        highlightedColors={highlightedColors}
        onClick={handleClick}
        style={{ maxWidth: '220px', margin: '0 auto' }}
      />
    </div>
  );
};
